// ---------------------------------------------------------------------------
// Bell Operations Console — sample data
// ---------------------------------------------------------------------------
// Used until CONFIG.DEMO = false (or as a graceful fallback if the live API is
// unreachable). demoWeek() synthesises a believable week: a weekly Saturday
// holiday, one power-cut afternoon and one faulty-amplifier day.
// ---------------------------------------------------------------------------
import {addDays, hash, iso, minutes, parseISO, rng, todayISO} from "../utils/bellStatusUtils";

export const DEMO_SCHOOLS = [
    {id: "sch-1041", name: "Shree Janata Secondary School", name_ne: "श्री जनता माध्यमिक विद्यालय", code: "DG-1041", district: "Lalitpur", students: 812, type: "Community"},
    {id: "sch-1088", name: "Kathmandu Model Higher Secondary", name_ne: "काठमाडौँ मोडेल उच्च मा.वि.", code: "DG-1088", district: "Kathmandu", students: 1460, type: "Institutional"},
    {id: "sch-1123", name: "Himalaya Boarding School", name_ne: "हिमालय बोर्डिङ स्कुल", code: "DG-1123", district: "Bhaktapur", students: 540, type: "Institutional"},
    {id: "sch-1190", name: "Shree Saraswati Ma.Vi.", name_ne: "श्री सरस्वती मा.वि.", code: "DG-1190", district: "Kaski", students: 396, type: "Community"},
    {id: "sch-1204", name: "Little Angels Montessori", name_ne: "लिटिल एन्जल्स मन्टेश्वरी", code: "DG-1204", district: "Chitwan", students: 208, type: "Montessori"},
    {id: "sch-1255", name: "Everest Technical Campus", name_ne: "एभरेस्ट प्राविधिक क्याम्पस", code: "DG-1255", district: "Morang", students: 1105, type: "Campus"},
];

export const DEMO_PERIODS = [
    {id: "p0", name: "Assembly", name_ne: "सभा", time: "09:55", strikes: 1, kind: "assembly", zones: ["Ground", "Block A", "Block B"]},
    {id: "p1", name: "Period 1", name_ne: "पिरियड १", time: "10:15", strikes: 1, kind: "period", zones: ["Block A", "Block B"]},
    {id: "p2", name: "Period 2", name_ne: "पिरियड २", time: "11:00", strikes: 2, kind: "period", zones: ["Block A", "Block B"]},
    {id: "p3", name: "Period 3", name_ne: "पिरियड ३", time: "11:45", strikes: 3, kind: "period", zones: ["Block A", "Block B"]},
    {id: "pl", name: "Lunch", name_ne: "खाजा", time: "12:30", strikes: 4, kind: "break", zones: ["Ground", "Block A", "Block B", "Canteen"]},
    {id: "p4", name: "Period 4", name_ne: "पिरियड ४", time: "13:10", strikes: 4, kind: "period", zones: ["Block A", "Block B"]},
    {id: "p5", name: "Period 5", name_ne: "पिरियड ५", time: "13:55", strikes: 5, kind: "period", zones: ["Block A", "Block B"]},
    {id: "p6", name: "Period 6", name_ne: "पिरियड ६", time: "14:40", strikes: 6, kind: "period", zones: ["Block A", "Block B"]},
    {id: "p7", name: "Period 7", name_ne: "पिरियड ७", time: "15:25", strikes: 7, kind: "period", zones: ["Block A", "Block B"]},
    {id: "pd", name: "Dismissal", name_ne: "छुट्टी", time: "16:10", strikes: 2, kind: "dismissal", zones: ["Ground", "Block A", "Block B", "Canteen"]},
];

export const FAULTS = [
    {code: "DG-E12", en: "Amplifier did not acknowledge the ring on Zone B. Check the 100V line and amp power.", ne: "ब्लक बी क्षेत्रको एम्प्लिफायरले घण्टी स्वीकार गरेन। १००V लाइन र एम्पको पावर जाँच्नुहोस्।"},
    {code: "DG-E07", en: "Tone file missing on the controller. Re-sync the tone pack from the dashboard.", ne: "नियन्त्रकमा ध्वनि फाइल भेटिएन। ड्यासबोर्डबाट टोन प्याक पुनः सिंक गर्नुहोस्।"},
    {code: "DG-E21", en: "Controller was powered down at ring time — mains lost, battery drained.", ne: "घण्टी बज्ने बेला नियन्त्रक बन्द थियो — बिजुली गयो, ब्याट्री सकियो।"},
    {code: "DG-E33", en: "Clock drift over 45s; scheduler skipped the slot and resynced with NTP.", ne: "घडी ४५ से. भन्दा बढी फरक पर्‍यो; तालिकाले स्लट छोड्यो र NTP सँग पुनः मिलायो।"},
];

// schoolMeta (optional) is the real school picked from the dropdown; when
// present its identity (name/code/district…) is used so the sampled week reads
// as belonging to the selected school. The RNG is still seeded by schoolId so a
// given school always yields the same believable week.
export function demoWeek(schoolId, from, to, lang = "en", schoolMeta = null) {
    const base = DEMO_SCHOOLS.find((x) => x.id === schoolId);
    const s = schoolMeta ? {...(base || {}), ...schoolMeta, id: schoolId} : base || DEMO_SCHOOLS[0];
    const nowKey = todayISO(), now = new Date(), nowMin = now.getHours() * 60 + now.getMinutes();

    const keys = [];
    let cur = parseISO(from), end = parseISO(to);
    while (cur <= end) {
        keys.push({key: iso(cur), wd: cur.getDay()});
        cur = addDays(cur, 1);
    }
    const past = keys.filter((k) => k.wd !== 6 && k.key <= nowKey).map((k) => k.key);
    const offlineDay = past.length ? past[hash(s.id + from) % past.length] : null;
    const failDay = past.length ? past[past.length - 1] : null;

    const days = keys.map(({key, wd}) => {
        const r = rng(s.id + key), isSat = wd === 6;
        const day = {
            date: key,
            holiday: isSat,
            holiday_name: isSat ? (lang === "ne" ? "साप्ताहिक बिदा" : "Weekly holiday") : null,
            events: [],
        };
        if (isSat) return day;
        DEMO_PERIODS.forEach((p) => {
            const pm = minutes(p.time);
            if (key > nowKey || (key === nowKey && pm > nowMin + 2)) {
                day.events.push({
                    period_id: p.id, status: "pending", scheduled_at: p.time, actual_at: null,
                    delay_sec: null, strikes_played: 0, zones: p.zones, device_online: true,
                });
                return;
            }
            let status = "played", delay = Math.round(r() * 4), err = null, online = true, strikes = p.strikes;
            if (key === offlineDay && pm >= 13 * 60 + 50) {
                status = "offline"; online = false; strikes = 0; err = FAULTS[2];
            } else if (key === failDay && (p.id === "p3" || p.id === "p6")) {
                status = "failed"; strikes = 0; err = FAULTS[hash(key + p.id) % 2 ? 0 : 1];
            } else if (r() < 0.14) {
                status = "late"; delay = 62 + Math.floor(r() * 170);
            } else if (r() < 0.04) {
                status = "failed"; strikes = 0; err = FAULTS[3];
            }
            const actual =
                status === "played" || status === "late"
                    ? (() => {
                        const m = pm + Math.round(delay / 60);
                        return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
                    })()
                    : null;
            day.events.push({
                period_id: p.id, status, scheduled_at: p.time, actual_at: actual,
                delay_sec: status === "played" || status === "late" ? delay : null,
                strikes_played: strikes, zones: p.zones, device_online: online,
                error_code: err ? err.code : null,
                error_message: err ? err[lang] || err.en : null,
                retried: status === "failed" && r() < 0.5,
            });
        });
        return day;
    });

    return {
        school: {
            ...s,
            address: s.address || (s.district ? `${s.district}, Nepal` : ""),
            zones: s.zones || ["Ground", "Block A", "Block B", "Canteen"],
            device: {
                id: (s.code || s.id) + "-CTRL",
                model: "DG Controller v2",
                firmware: "2.4.1",
                online: true,
                last_seen: new Date(Date.now() - 1000 * 60 * (1 + (hash(s.id) % 6))).toISOString(),
            },
        },
        periods: DEMO_PERIODS,
        days,
    };
}
